import { useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * BundleButton — bottom-LEFT floating button. Lists Sage's canonical
 * subscription tiers with monthly + yearly prices. Clicking a price
 * routes to /tiers where the PayPal buttons live (the family runs on
 * PayPal Phase 1 and Purser Phase 2).
 * Public. No mock prices.
 */
export default function BundleButton() {
  const [open, setOpen] = useState(false);
  const [tiers, setTiers] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || tiers) return;
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/sage/tiers`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        setTiers(d);
      } catch (e) {
        setErr(e.message);
      }
    })();
  }, [open, tiers]);

  const buy = (lookup_key) => {
    // Route to /tiers where PayPal is armed.
    window.location.href = `/tiers#${encodeURIComponent(lookup_key)}`;
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="bundle-button-open"
          className="fixed bottom-4 left-4 z-40 border border-sage-warn bg-sage-bg text-sage-warn w-20 h-20 text-[10px] uppercase tracking-[0.1em] leading-tight hover:bg-sage-warn hover:text-sage-bg transition-colors duration-150 shadow-lg flex items-center justify-center text-center px-2"
        >
          Members
          <br />
          &amp; deals
        </button>
      )}

      {open && (
        <div
          data-testid="bundle-modal"
          className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-sage-bg border border-sage-border shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-sage-border">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-faint">Memberships</div>
                <div className="text-lg text-sage-text tracking-tight">Real prices. Cancel any time. Refund on request.</div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="bundle-modal-close"
                className="text-xs text-sage-faint hover:text-sage-text"
              >
                close ×
              </button>
            </div>

            <div className="p-4 space-y-4">
              {err && <div className="text-xs text-sage-warn">Could not load: {err}</div>}
              {!tiers && !err && <div className="text-xs text-sage-faint">Loading…</div>}
              {tiers && !tiers.configured && (
                <div className="text-xs text-sage-faint sage-prose italic">
                  Checkout runs through PayPal on the <a href="/tiers" className="underline hover:text-sage-accent">Tiers page</a>. Tap any price below to jump there.
                </div>
              )}
              {tiers?.tiers?.map((t) => (
                <div
                  key={t.id}
                  data-testid={`bundle-item-${t.id}`}
                  className="border border-sage-border p-4 hover:border-sage-accent transition-colors duration-150 space-y-3"
                >
                  <div>
                    <div className="text-lg text-sage-text tracking-tight">{t.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint">{t.tagline}</div>
                  </div>
                  <ul className="list-disc list-inside space-y-1 text-xs text-sage-muted sage-prose">
                    {(t.features || []).map((f, i) => (
                      <li key={i}>{f}</li>
                    ))}
                  </ul>
                  <div className="grid sm:grid-cols-2 gap-2 pt-2">
                    {(t.prices || []).map((p) => (
                      <button
                        key={p.lookup_key}
                        data-testid={`bundle-buy-${p.lookup_key}`}
                        onClick={() => buy(p.lookup_key)}
                        className="border border-sage-accent text-sage-accent px-3 py-2 text-xs hover:bg-sage-accent hover:text-sage-bg transition-colors duration-150 flex flex-col items-start"
                      >
                        <span className="text-lg font-bold tabular-nums">${p.amount_usd?.toLocaleString()}</span>
                        <span className="text-[10px] uppercase tracking-[0.15em]">
                          per {p.interval} · pay with PayPal →
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <div className="text-[10px] text-sage-faint italic pt-2 border-t border-sage-border sage-prose">
                Every dollar is public on the <a href="/sage-ledger" className="underline hover:text-sage-accent">Ledger</a>. Above the $20M/year ceiling, revenue splits four ways: 50% to active users, 25% to the Resilience parent, 20% to the homeless / disabled / in-crisis / disaster relief funds, 5% to the whistleblower refuge. The <a href="/refund-door" data-testid="bundle-modal-refund-door-link" className="underline hover:text-sage-accent">refund door</a> is one click, no argument, honest reason logged.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
